import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Icon, Typography } from '@packages/ui/shared';
import { colors } from '@packages/core/styles';
import type { VehicleOption } from './associate-vehicles-picker';

type Props = {
    allVehicles: boolean;
    vehicles: VehicleOption[];
    onEdit: () => void;
};

const PREVIEW_COUNT = 3;

const useStyles = createUseStyles({
    card: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        padding: '12px 14px',
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
    },
    line: {
        fontSize: 14,
        color: colors.textDarkPrimary,
    },
    more: {
        fontSize: 13,
        fontWeight: 600,
        color: colors.blueOne,
    },
    edit: {
        flex: '0 0 auto',
        border: 'none',
        background: 'none',
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
    },
});

export const AssociatedVehiclesSummary: FC<Props> = ({ allVehicles, vehicles, onEdit }) => {
    const { t } = useTranslation();
    const classes = useStyles();

    return (
        <div className={classes.card}>
            <div className={classes.list}>
                {allVehicles ? (
                    <Typography variant="label">
                        {t('stisPublications:associateVehicles.allVehicles', 'All vehicles')}
                    </Typography>
                ) : (
                    <>
                        {vehicles.slice(0, PREVIEW_COUNT).map((vehicle) => (
                            <span key={vehicle.vehicleId} className={classes.line}>
                                {vehicle.description}
                            </span>
                        ))}
                        {vehicles.length > PREVIEW_COUNT && (
                            <span className={classes.more}>
                                {t('stisPublications:associateVehicles.andMore', {
                                    count: vehicles.length - PREVIEW_COUNT,
                                    defaultValue: '+{{count}} more',
                                })}
                            </span>
                        )}
                    </>
                )}
            </div>

            <button
                type="button"
                className={classes.edit}
                aria-label={t('stisPublications:associateVehicles.edit', 'Edit associated vehicles')}
                onClick={onEdit}
            >
                <Icon name="pencil" size={20} color="blueOne" />
            </button>
        </div>
    );
};
